<script lang="ts">
    import {datatypes, type FieldId} from 'src/typings';
    import type {GridField, HierarchicalRecordRow, SimpleGridFieldValue} from '../../../lib/types';
    import type {Column, Row} from '@tanstack/svelte-table';
    import type {FieldIdAndFieldValue} from 'src/features/record-grid/components/cells/data-cell/types';
    import type {FieldTypeDefinition} from 'src/features/record/field-types/types';
    import type {RecordGridDataManager} from 'src/features/record-grid/lib/record-grid-data-manager';
    import type {NonEmptyArray} from 'shared/utils/array-utils';
    import {asNotEmpty, isEmpty} from 'shared/utils/array-utils';
    import {isNullOrUndefined} from 'shared/utils/custom-utils';
    import {resolveGridFieldAttributeValue} from '../../../lib/grid-utils';
    import {getGridCellContext} from 'shared/ui-widgets/grid/grid-cell/grid-cell.context';
    import {generateIdOfInitialField, isInvalid} from 'src/features/record-grid/components/cells/data-cell/utils';
    import {hasOriginRecordReference, isErrorGridFieldValue, isOptionGridFieldValue} from 'src/features/record-grid/lib/types-utils';
    import ReferenceFieldValue from './values/ReferenceFieldValue.svelte';
    import ScalarFieldValue from './values/ScalarFieldValue.svelte';
    import EditableFieldValueContainer from './EditableFieldValueContainer.svelte';
    import ErrorFieldValue from './values/ErrorFieldValue.svelte';
    import OptionFieldValue from './values/OptionFieldValue.svelte';

    export let values: GridField<SimpleGridFieldValue>[] | undefined;
    export let column: Column<HierarchicalRecordRow>;
    export let fieldType: FieldTypeDefinition;
    export let row: Row<HierarchicalRecordRow>;
    export let recordGridDataManager: RecordGridDataManager;

    const {contentDependentState$} = getGridCellContext();

    $: fields = normalize(values);
    $: enabled = resolveGridFieldAttributeValue(column.columnDef.meta.enabled, row.original.fond.id);
    $: editable = resolveGridFieldAttributeValue(column.columnDef.meta.editable, row.original.fond.id) && (row.original.editPermission.allowed || (row.original.editLockStatus.locked && (row.original.editLockStatus.cause?.conflictingFieldTypeIds ?? []).includes(fieldType.id)));
    $: required = resolveGridFieldAttributeValue(column.columnDef.meta.required, row.original.fond.id);
    $: contentDependentState$.set({disabled: !enabled, readOnly: !editable, invalid: isInvalid(fields, required)});

    function editCell(value: SimpleGridFieldValue, fieldId: FieldId) {
        recordGridDataManager.queueUpdate({row, fieldId, value, regenerate: false});
    }

    function normalize(gridFields: GridField<SimpleGridFieldValue>[]): NonEmptyArray<FieldIdAndFieldValue<GridField<SimpleGridFieldValue>>> {
        if (isNullOrUndefined(gridFields) || isEmpty(gridFields)) {
            return [{value: null, fieldId: generateIdOfInitialField(fieldType.id)}];
        }

        return asNotEmpty(gridFields.map((gridField) => ({value: gridField, fieldId: gridField.fieldId})));
    }
</script>

{#if enabled}
    {#if fields.length === 1}
        {@const field = fields[0]}
        <div title={field.fieldId}>
            <EditableFieldValueContainer value={field.value}
                                         {editable}
                                         {fieldType}
                                         {row}
                                         on:cell-edit={(event) => editCell(event.detail, field.fieldId)}/>
        </div>
    {:else}
        <div class="value-container">
            <ul>
                {#each fields as field}
                    <li title={field.fieldId}>
                        {#if hasOriginRecordReference(field.value) && fieldType.valueDatatype?.name === datatypes.TEXT}
                            <ReferenceFieldValue value={field.value}/>
                            <!-- ReferenceFieldValue already uses ChipTag -->
                        {:else if isErrorGridFieldValue(field.value)}
                            <ErrorFieldValue value={field.value}/>
                        {:else if isOptionGridFieldValue(field.value)}
                            <OptionFieldValue value={field.value}/>
                            <!-- OptionFieldValue already uses ChipTag -->
                        {:else}
                            <ScalarFieldValue value={field.value}
                                              valueFormat={fieldType.valueFormat}
                                              datatype={fieldType.valueDatatype}/>
                        {/if}
                    </li>
                {/each}
            </ul>
        </div>
    {/if}
{/if}

<style lang="less">
    ul {
        display: inline-flex;
        gap: 3px;
    }

    li {
        display: inline;
    }

    .value-container {
        display: flex;
        align-items: center;
        position: relative;
    }
</style>